import api from 'core/util/api';
import $globalFilters from 'app/stores/globalFilter/$globalFilters';
import createLookup from 'app/util/lookups/createLookup';
import { deviceIndex, DeviceIndexEntry } from 'app/util/lookups/deviceIndex';
import type { DeviceRangeResponse } from '@kentik/ui-shared/types/deviceIndex.types';

const STALE_TIME = 5 * 60 * 1000;

interface DeviceRangeParams {
  planId: string;
  limit: number;
  offset?: number;
}

const getDeviceRangePage = ({ planId, limit, offset = 0 }: DeviceRangeParams): Promise<DeviceRangeResponse> => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset), planId });
  const { scopeGeneration } = $globalFilters;

  return api.get(`/api/ui/lookups/devices?${params}`, { showErrorToast: false }).then((page: DeviceRangeResponse) => {
    // skip if the scope changed while the request was in flight
    if ($globalFilters.scopeGeneration === scopeGeneration) {
      deviceIndex.ingest(page.devices);
    }

    return page;
  });
};

interface PlanDevicePage {
  id: string;
  devices: DeviceIndexEntry[];
  hasMore: boolean;
  restricted: boolean;
}

const planDevicePages = createLookup<PlanDevicePage>(
  'planDevicePage',
  (ids) =>
    Promise.all(
      ids.map((id) => {
        const [planId, pageSize, pageIndex] = id.split(':');
        const limit = Number(pageSize);

        return getDeviceRangePage({ planId, limit, offset: limit * Number(pageIndex) }).then((page) => ({
          id,
          ...page
        }));
      })
    ),
  { staleTime: STALE_TIME }
);

export const usePlanDevicePage = (planId: string | number, pageIndex: number, pageSize = 20) => {
  const { data, isLoading, error } = planDevicePages.useLookup(`${planId}:${pageSize}:${pageIndex}`);

  return {
    devices: data?.devices ?? [],
    hasMore: !!data?.hasMore,
    restricted: !!data?.restricted,
    isLoading,
    error
  };
};

$globalFilters.registerScopeChangeHandler(() => planDevicePages.clear());
